import { Breadcrumbs, BreadcrumbTemplateEnum, Page, PageHeader, PageInner, PageSection } from "@app/shared";
import { TileProps } from "@app/shared/interfaces/dashboard";
import { useContext } from "react";
import * as React from "react";
import { Link } from "react-router-dom";
import { RoleRestricted, Roles } from "@app/user";
import { Features } from "../feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";

export const AdminDashboard: React.FC<React.PropsWithChildren<unknown>> = () => {

  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const [tiles] = React.useState<TileProps[]>([
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN],
      icon: QesIcons.FontAwesome.University,
      route: "client-administration",
      title: "Client Administration",
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN],
      icon: QesIcons.FontAwesome.Users,
      route: "user-administration",
      title: "User Administration",
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN],
      icon: QesIcons.FontAwesome.ClockOutline,
      route: "user-sessions",
      title: "User Sessions",
    },
    {
      allowedRoles: [Roles.SOFTWARE_LICENSE_ADMIN],
      icon: QesIcons.FontAwesome.FilesOutline,
      route: "client-license/manager",
      title: "QAS Software Licenses"
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.SOFTWARE_LICENSE_ADMIN],
      icon: QesIcons.FontAwesome.Laptop,
      route: "software-releases",
      title: "QAS Software Releases"
    },
    {
      allowedRoles: [Roles.CLIENT_ADMIN],
      icon: QesIcons.FontAwesome.UserMd,
      route: "provider-results",
      title: "Provider Results"
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN],
      icon: QesIcons.FontAwesome.SignIn,
      route: "login-statistics",
      title: "User Login Stats"
    },
    {
      allowedRoles: [Roles.QUEST_DOCUMENT_ADMIN],
      icon: QesIcons.FontAwesome.Pencil,
      route: "manage-tutorials",
      title: "Manage Tutorials"
    },
    {
      allowedRoles: [Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN],
      icon: QesIcons.FontAwesome.ObjectGroup,
      route: "role-groups",
      title: "Role Groups",
    },
    {
      allowedRoles: [Roles.INTERNAL_ONLY_CMS_EXCEPTION_DOCUMENT_MANAGER],
      icon: QesIcons.FontAwesome.FilePdfOutline,
      route: "specialty-group-exception",
      title: "Specialty Group Exception Template",
    },
    {
      allowedRoles: [Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN, Roles.RESOURCE_ADMIN],
      icon: QesIcons.FontAwesome.Folder,
      requiredFeatureFlag: Features.MANAGE_QES_TEMPLATES,
      route: "manage-templates",
      title: "Manage Templates",
    },
  ]);

  return (
    <Page documentTitle="Admin Dashboard">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Admin} items={["Dashboard"]} />
      <PageInner>
        <PageHeader iconName={QesIcons.FontAwesome.Cogs} title="Admin Dashboard" disableHR={false} />
        <PageSection>
          <div className="container mt-3">
            {tiles.map((tile) =>             
              (!tile.requiredFeatureFlag &&
                <Tile key={tile.title} icon={tile.icon} route={tile.route} title={tile.title} allowedRoles={tile.allowedRoles} isV1={tile.isV1} />)
              || (!!tile.requiredFeatureFlag && featureFlags.has(tile.requiredFeatureFlag) && 
                <Tile key={tile.title} icon={tile.icon} route={tile.route} title={tile.title} allowedRoles={tile.allowedRoles} isV1={tile.isV1} />)
            )}
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
};

const Tile: React.FC<React.PropsWithChildren<TileProps>> = ({ icon, route = "#", title, allowedRoles, isV1 }) => {
  if (isV1) {
    return (
      <RoleRestricted allowedRoles={allowedRoles}>
        <a href={`${window.uiEndpoint}/${route}`} className="dashboard-tile col-4 mb-3">
          <QesIcon iconName={icon} isDecorative={true} />
          <h6>{title}</h6>
        </a>
      </RoleRestricted >
    );
  } else {
    return (
      <RoleRestricted allowedRoles={allowedRoles}>
        <Link to={`/admin/${route}`} className="dashboard-tile col-4">
          <QesIcon iconName={icon} isDecorative={true} />
          <h6>{title}</h6>
        </Link>
      </RoleRestricted>
    );
  }
};
